import { useCallback, useRef, useState } from 'react'
import type { RobotModeMetadata } from 'api/RobotApi'
import store from 'store/store'
import { robotThunks } from 'store/thunks/robot'
import { ROBOT_MODES } from 'utils/constants'

import {
  MAP_RECORDING_CONFIRM_FALLBACK_MS,
  MODE_SWITCH_CONFIRM_FALLBACK_MS,
  waitForModeSwitchConfirmation,
} from './modeSwitchConfirmation'

export type ModeSwitchConfirmMessages = {
  requestFailed: string
  noConfirmation: string
}

export type ModeSwitchConfirmOutcome = 'confirmed' | 'timeout' | 'aborted' | 'failed'

/**
 * Shared dialog wait session for service-tab mode switches that must not close
 * until MCS feedback confirms the target mode (map / route recording, …).
 */
export function useModeSwitchConfirmWait() {
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef(false)

  const begin = useCallback(() => {
    abortRef.current = false
    setError(null)
  }, [])

  const abort = useCallback(() => {
    abortRef.current = true
    setError(null)
  }, [])

  const waitForTargetMode = useCallback(
    async (targetMode: ROBOT_MODES, longerWait: boolean, messages: ModeSwitchConfirmMessages) => {
      const outcome = await waitForModeSwitchConfirmation(
        targetMode,
        () => {
          const info = store.getState().robot.robotInfo
          return { activeMode: info?.activeMode, pending: info?.modeSwitchPending }
        },
        {
          fallbackMs: longerWait ? MAP_RECORDING_CONFIRM_FALLBACK_MS : MODE_SWITCH_CONFIRM_FALLBACK_MS,
          keepWaitingAfterPendingClear: longerWait,
          isAborted: () => abortRef.current,
        }
      )
      if (outcome === 'aborted') {
        return 'aborted'
      }
      if (outcome === 'confirmed') {
        setError(null)
        return 'confirmed'
      }
      if (!abortRef.current) {
        setError(messages.noConfirmation)
      }
      return 'timeout'
    },
    []
  )

  const requestAndWait = useCallback(
    async (
      robotId: number,
      mode: ROBOT_MODES,
      metadata: RobotModeMetadata | undefined,
      actionKey: string,
      messages: ModeSwitchConfirmMessages
    ): Promise<ModeSwitchConfirmOutcome> => {
      begin()
      try {
        await store.dispatch(robotThunks.setRobotMode(robotId, mode, metadata, { actionKey }))
      } catch {
        if (!abortRef.current) {
          setError(messages.requestFailed)
        }
        return 'failed'
      }
      return waitForTargetMode(mode, mode === ROBOT_MODES.MODE_MAPPING, messages)
    },
    [begin, waitForTargetMode]
  )

  const requestStopAndWait = useCallback(
    async (
      robotId: number,
      actionKey: string,
      messages: ModeSwitchConfirmMessages,
      options?: { longerWait?: boolean }
    ): Promise<ModeSwitchConfirmOutcome> => {
      begin()
      try {
        await store.dispatch(robotThunks.setRobotNotSetMode(robotId, { actionKey }))
      } catch {
        if (!abortRef.current) {
          setError(messages.requestFailed)
        }
        return 'failed'
      }
      return waitForTargetMode(ROBOT_MODES.MODE_NOT_SET, Boolean(options?.longerWait), messages)
    },
    [begin, waitForTargetMode]
  )

  return { error, setError, begin, abort, requestAndWait, requestStopAndWait }
}
